// Admin: find sessions across every patient and delete many at once. Filters narrow the
// list (study ID, site, program, mode, status, debug, dates); quick picks select the usual
// clean-ups (debug sessions, unfinished ones, nothing counted). Deleting uses the shared
// bar: a preview of what goes, then "delete N".

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, type SessionRow } from "../api";
import { useAuth } from "../auth";
import { AllCheck, BulkBar, plural, RowCheck, rowKey, useSelection } from "../components/Bulk";
import { useLoad } from "../hooks";

const PAGE = 200;
const DAY_MS = 24 * 3600 * 1000;

export function DataCleanupPage() {
  const { user } = useAuth();
  const rows = useLoad(() => api.allSessions(), []);
  const sites = useLoad(() => api.sites(), []);
  const counts = useLoad(() => api.counts(), []);
  const sel = useSelection();
  const [f, setF] = useState({ study: "", site: "", program: "", mode: "", status: "", demo: "", from: "", to: "" });
  const [limit, setLimit] = useState(PAGE);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setF({ ...f, [k]: e.target.value });
    setLimit(PAGE);
  };

  const all = rows.data ?? [];
  const programs = useMemo(() => [...new Set(all.map((r) => r.program ?? ""))].sort(), [all]);
  const match = (r: SessionRow) => {
    return (!f.study || r.study_id.toLowerCase().includes(f.study.trim().toLowerCase()))
      && (!f.site || String(r.site_id) === f.site)
      && (!f.program || (r.program ?? "") === f.program)
      && (!f.mode || r.mode === f.mode)
      && (!f.status || (f.status === "unfinished" ? r.status !== "complete" : f.status === "nothing" ? r.counted === 0 : r.status === f.status))
      && (!f.demo || (f.demo === "only" ? r.demo : f.demo === "patient" ? r.patient_demo : !r.demo))
      && (!f.from || localDay(r.started_at) >= f.from)
      && (!f.to || localDay(r.started_at) <= f.to);
  };
  const filtered = all.filter(match);
  const shown = filtered.map((r) => rowKey("sessions", r.id));
  const visible = filtered.slice(0, limit);
  const order = visible.map((r) => rowKey("sessions", r.id));
  const now = Date.now();

  if (user?.role !== "admin") return <p>Admins only.</p>;
  return (
    <>
      <p className="small"><Link to="/admin">← Admin</Link></p>
      <h1>Clean up data</h1>
      <div className="card">
        <p style={{ marginTop: 0 }}>Every session of every patient, newest first. Narrow the list, tick what should go (or use a
          quick pick, then <em>All shown</em>), and delete them together. A session takes its tests, trials and
          notifications with it; the patient stays. To delete patients themselves, or programs, sites, stations and
          accounts, tick them on their own pages.</p>
        {counts.data && (
          <p className="small muted">In the database: {(["patients", "sessions", "tests", "programs", "sites", "stations", "displays", "display_models", "users"] as const)
            .map((k) => plural(counts.data![k] ?? 0, k)).join(" · ")}</p>
        )}
        <div className="filters">
          <label>Study ID contains<input value={f.study} onChange={set("study")} style={{ width: "9rem" }} /></label>
          <label>Site
            <select aria-label="Site" value={f.site} onChange={set("site")}>
              <option value="">any</option>
              {(sites.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label>Program
            <select aria-label="Program" value={f.program} onChange={set("program")}>
              <option value="">any</option>
              {programs.map((p) => <option key={p} value={p}>{p || "none"}</option>)}
            </select>
          </label>
          <label>Mode
            <select aria-label="Mode" value={f.mode} onChange={set("mode")}>
              <option value="">any</option><option value="clinic">clinic</option><option value="home">home</option>
            </select>
          </label>
          <label>Status
            <select aria-label="Status" value={f.status} onChange={set("status")}>
              <option value="">any</option><option value="complete">complete</option>
              <option value="unfinished">unfinished</option><option value="nothing">nothing counted</option>
            </select>
          </label>
          <label>Debug
            <select aria-label="Debug" value={f.demo} onChange={set("demo")}>
              <option value="">any</option><option value="only">debug sessions (checks skipped)</option><option value="none">not debug</option>
              <option value="patient">patients skipping checks (debug mode)</option>
            </select>
          </label>
          <label>From<input type="date" value={f.from} onChange={set("from")} /></label>
          <label>To<input type="date" value={f.to} onChange={set("to")} /></label>
          <button className="secondary" onClick={() => { setF({ study: "", site: "", program: "", mode: "", status: "", demo: "", from: "", to: "" }); setLimit(PAGE); }}>
            Clear filters</button>
        </div>
        {rows.error && <div className="error">{rows.error}</div>}
        <BulkBar sel={sel} shown={shown} what="sessions" onDeleted={() => { rows.reload(); counts.reload(); }} presets={[
          { label: "Debug sessions", keys: filtered.filter((r) => r.demo).map((r) => rowKey("sessions", r.id)) },
          { label: "Patients in debug mode", keys: filtered.filter((r) => r.patient_demo).map((r) => rowKey("sessions", r.id)) },
          { label: "Unfinished, older than a day", keys: filtered.filter((r) => r.status !== "complete"
            && now - new Date(r.started_at).getTime() > DAY_MS).map((r) => rowKey("sessions", r.id)) },
          { label: "Nothing counted", keys: filtered.filter((r) => r.counted === 0).map((r) => rowKey("sessions", r.id)) },
        ]} />
        <p className="small muted">{filtered.length === all.length ? `${all.length} sessions` : `${filtered.length} of ${all.length} sessions match`}
          {filtered.length > visible.length && ` · showing the first ${visible.length}`}</p>
        <table>
          <thead><tr><th className="pick-col"><AllCheck sel={sel} keys={shown} label="Select all matching sessions" /></th>
            <th>Started</th><th>Study ID</th><th>Program</th><th>Mode</th><th>Status</th><th>Tests counted</th><th /></tr></thead>
          <tbody>
            {visible.map((r) => {
              const k = rowKey("sessions", r.id);
              return (
                <tr key={r.id} className={sel.has(k) ? "picked" : ""}>
                  <td className="pick-col"><RowCheck sel={sel} k={k} order={order} label={`Select session ${r.study_id} ${new Date(r.started_at).toLocaleString()}`} /></td>
                  <td>{new Date(r.started_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td><Link to={`/patients/${r.patient_id}`}>{r.study_id}</Link>{r.patient_demo && <> <span className="badge demo">debug patient</span></>}</td>
                  <td>{r.program ?? "—"}</td>
                  <td>{r.mode}{r.demo && <> <span className="badge demo">debug</span></>}</td>
                  <td>{r.status === "in_progress" ? "unfinished" : r.status}</td>
                  <td>{r.counted} of {r.tests}</td>
                  <td><Link to={`/sessions/${r.id}`}>Results</Link></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length > visible.length && (
          <button className="secondary" style={{ marginTop: "0.6rem" }} onClick={() => setLimit(limit + PAGE * 5)}>Show more</button>
        )}
      </div>
    </>
  );
}

function localDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
